// Offline phase 1 (PMU-270): the apps keep working with the internet down. What they last loaded
// is kept on the device and shown again offline; writes made offline wait in a queue and are sent,
// in order, when the internet returns. Everything is sealed with a device key that never leaves
// this device (AES-GCM, non-extractable, kept by IndexedDB's structured clone): children's names
// and photos are never stored in the clear. Each write carries an Idempotency-Key, and the server
// answers a repeat with its first answer, so a write sent twice is applied once.
//
// Only the apps turn this on (enableOffline): the console in a browser stays online-only.
import { openKv, type Kv } from "./kv.ts";

interface Sealed { iv: Uint8Array<ArrayBuffer>; data: ArrayBuffer }
export interface QueuedWrite {
  key: string; method: string; path: string; body?: unknown; label: string; at: number; tries?: number;
  /** When it was first refused with a conflict that can clear (409/404): retried for a while from then. */
  waitingSince?: number;
  /** Refusals that mean "nothing left to do" (an answer to a request already handed over). */
  doneIf?: string[];
  /** Refusals that, once the wait is over, are dropped quietly (an answer to a request that never reached the server). */
  dropIf?: string[];
}

/** How long a 409/404 that can clear is tried again, from the first refusal. */
const RETRY_WINDOW_MS = 10 * 60_000;
export interface FailedWrite { label: string; at: number; message: string }

let kv: Kv | null = null;
let aes: Promise<CryptoKey> | null = null;
let online = true;
let since: number | null = null; // when the device went offline
let flushing: Promise<void> | null = null;
let send: ((w: QueuedWrite) => Promise<Response>) | null = null;
const subs = new Set<() => void>();
// One current state object, replaced on every change: readers (React's useSyncExternalStore) get
// the same object until something changes, and a change made before they subscribed is not lost.
let state: OfflineState = { online: true, since: null, queued: 0, failed: [] };
const emit = () => { state = { online, since, queued, failed }; for (const f of subs) f(); };

/** Turns the offline store on (the apps only). `name` keeps each app's store apart. */
export function enableOffline(name: string, sender: (w: QueuedWrite) => Promise<Response>) {
  kv = openKv(name);
  send = sender;
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => { void flushQueue(); });
    setInterval(() => { void flushQueue(); }, 30_000);
  }
  void flushQueue();
}
export const offlineEnabled = () => kv !== null;

function key(): Promise<CryptoKey> {
  return (aes ??= (async () => {
    const have = await kv!.get<CryptoKey>("aes");
    if (have) return have;
    const k = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await kv!.set("aes", k);
    return k;
  })());
}
async function seal(value: unknown): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const bytes: Uint8Array<ArrayBuffer> = value instanceof ArrayBuffer ? new Uint8Array(value) : new TextEncoder().encode(JSON.stringify(value));
  return { iv, data: await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await key(), bytes) };
}
async function unseal(s: Sealed, raw = false): Promise<unknown> {
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: s.iv }, await key(), s.data);
  return raw ? plain : JSON.parse(new TextDecoder().decode(plain));
}

// ---------- online state ----------

export interface OfflineState { online: boolean; since: number | null; queued: number; failed: FailedWrite[] }
let queued = 0;
let failed: FailedWrite[] = [];
export const offlineState = (): OfflineState => state;
export function subscribeOffline(f: () => void) { subs.add(f); return () => { subs.delete(f); }; }
export function markOffline() { if (online) { online = false; since = Date.now(); emit(); } }
export function markOnline() { if (!online) { online = true; since = null; emit(); void flushQueue(); } }
export async function dismissFailed() { failed = []; await kv?.set("failed", failed); emit(); }

// ---------- what was last loaded ----------

/** Keeps a GET's answer (sealed) for when the device is offline. */
export async function cachePut(path: string, data: unknown) {
  if (!kv) return;
  await kv.set(`c:${path}`, { at: Date.now(), sealed: await seal(data) }).catch(() => undefined);
}
/** The last answer kept for a GET, and when it was loaded. */
export async function cacheGet<T>(path: string): Promise<{ at: number; data: T } | null> {
  if (!kv) return null;
  const hit = await kv.get<{ at: number; sealed: Sealed }>(`c:${path}`).catch(() => undefined);
  if (!hit) return null;
  try { return { at: hit.at, data: (await unseal(hit.sealed)) as T }; } catch { return null; }
}
/** Photos and other blobs, sealed the same way. */
export async function blobPut(path: string, blob: Blob) {
  if (!kv) return;
  await kv.set(`b:${path}`, { type: blob.type, sealed: await seal(await blob.arrayBuffer()) }).catch(() => undefined);
}
export async function blobGet(path: string): Promise<Blob | null> {
  if (!kv) return null;
  const hit = await kv.get<{ type: string; sealed: Sealed }>(`b:${path}`).catch(() => undefined);
  if (!hit) return null;
  try { return new Blob([(await unseal(hit.sealed, true)) as ArrayBuffer], { type: hit.type }); } catch { return null; }
}
/** Signing out: nothing of this account stays on the device (the queue goes too). */
export async function forgetOffline() {
  if (!kv) return;
  await kv.clear();
  aes = null; queued = 0; failed = [];
  emit();
}

// ---------- writes made offline ----------

async function readQueue(): Promise<QueuedWrite[]> {
  const s = await kv!.get<Sealed>("q");
  if (!s) return [];
  try { return (await unseal(s)) as QueuedWrite[]; } catch { return []; }
}
async function writeQueue(q: QueuedWrite[]) { await kv!.set("q", await seal(q)); queued = q.length; emit(); }

/** Keeps a write to send when the internet returns. */
export async function enqueueWrite(w: QueuedWrite) {
  if (!kv) throw new Error("offline store off");
  const q = await readQueue();
  q.push(w);
  await writeQueue(q);
}

/**
 * Sends the queue, oldest first. A network failure stops it (order is kept); a 5xx, 401, 409 or
 * 429 waits for the next try; any other answer, success or refusal, takes the write off the queue
 * (a refusal is listed for the person, it would be refused again).
 */
export function flushQueue(): Promise<void> {
  if (!kv || !send) return Promise.resolve();
  return (flushing ??= (async () => {
    try {
      let q = await readQueue();
      queued = q.length; emit();
      while (q.length) {
        const w = q[0]!;
        let res: Response;
        try { res = await send!(w); } catch { markOffline(); return; }
        markOnline();
        // A 401 is "not signed in yet": the app sends the queue as it starts, before the saved
        // sign-in has loaded. The next try carries it; signing out clears the queue.
        if (res.status >= 500 || res.status === 429 || res.status === 401) return;
        const body = res.ok ? {} : (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        // A 409 is "still being saved" or a conflict that can clear (the bus's taps not sent yet).
        // A 404 can also clear: an answer to a door request that the door, back online too, has
        // not sent yet (phase 2, the request came over the Wi-Fi). Both are tried again for
        // 10 minutes from the first refusal (by time: tries come in bursts when the internet
        // returns) before they are listed as not accepted.
        const done = !!body.error && !!w.doneIf?.includes(body.error);
        const since = w.waitingSince ?? Date.now();
        if ((res.status === 409 || res.status === 404) && !done && (body.error === "in_progress" || Date.now() - since < RETRY_WINDOW_MS)) {
          if (body.error !== "in_progress") { q[0] = { ...w, tries: (w.tries ?? 0) + 1, waitingSince: since }; await writeQueue(q); }
          return;
        }
        const dropped = !!body.error && !!w.dropIf?.includes(body.error);
        if (!res.ok && !done && !dropped) {
          failed = [...failed, { label: w.label, at: w.at, message: body.message ?? body.error ?? `HTTP ${res.status}` }].slice(-20);
          await kv!.set("failed", failed);
        }
        q = q.slice(1);
        await writeQueue(q);
      }
    } finally { flushing = null; }
  })());
}

/** Loads the counts at start, so the banner is right before the first send. */
export async function loadOfflineState() {
  if (!kv) return;
  queued = (await readQueue()).length;
  failed = (await kv.get<FailedWrite[]>("failed")) ?? [];
  emit();
}
